<template>
	<div class="section">
		<div class="container is-medium">
			<div class="section-body">
				<h2 class="section-subtitle is-black">
					Welcome to RediR™!
				</h2>
				<h3 class="section-subtitle is-small-thin">
					Before you proceed, please answer these quick questions
				</h3>
				<div class="line" />
				<h2 class="section-subtitle is-small is-black">
					What do you plan to use RediR™ for?
				</h2>
				<div class="fields">
					<div
						v-for="intention in intentions"
						:key="intention.id"
						class="field"
						:class="{ 'is-active' : intention.selected }"
						@click="intention.selected = !intention.selected"
					>
						{{ intention.name }}
					</div>
				</div>
				<div class="section-items">
					<ValidationObserver
						ref="observer"
						v-slot="{ valid }"
						tag="form"
						class="form"
						@submit.prevent="signup"
					>
						<div class="item">
							<div class="section-description">
								Full name
							</div>
							<AppInput
								v-model="form.FullName"
								size="full"
								:rules="{ required: true }"
								mode="aggressive-touch"
								placeholder="Enter your full name"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Username
							</div>
							<AppInput
								v-model="form.UserName"
								size="full"
								:rules="{ required: true }"
								mode="aggressive-touch"
								placeholder="Enter your username"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Email
							</div>
							<AppInput
								v-model="form.EMailAddress"
								size="full"
								:rules="{ required: true, email: true }"
								mode="aggressive-touch"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Country
							</div>
							<AppSelect
								v-model="selectedCountry"
								:options="countries"
								option-label="name"
								option-value="name"
								required
								searchable
								placeholder="Type or select your country"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Youtube channel URL
							</div>
							<AppInput
								v-model="form.YouTubeChannelURL"
								size="full"
								:rules="{ url: true }"
								mode="aggressive-touch"
								placeholder="Enter your Youtube channel URL"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Password
							</div>
							<AppInput
								v-model="form.Password"
								type="password"
								size="full"
								vid="confirm"
								:rules="{ required: true }"
								mode="aggressive-touch"
								placeholder="Enter your password"
							/>
						</div>
						<div class="item">
							<div class="section-description">
								Confirm password
							</div>
							<AppInput
								v-model="form.confirmPassword"
								type="password"
								size="full"
								:rules="{ required: true, confirmed: 'confirm' }"
								mode="aggressive-touch"
								placeholder="Enter your password again"
							/>
						</div>
						<div class="item button">
							<AppButton
								size="full"
								color="primary"
								:disabled="!valid"
							>
								Sign up
							</AppButton>
						</div>
					</ValidationObserver>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import { en as countries } from '@/data/countries';
import IntentionList from '@/data/intentions';

export default {
	name: 'Registration',

	components: {
		ValidationObserver,
	},

	props: {
		invitationCode: {
			type: String,
			required: true,
		},
	},

	data() {
		return {
			form: {
				UserName: '',
				FullName: '',
				Password: '',
				confirmPassword: '',
				EMailAddress: '',
				Country: '',
				Intention: '',
				YouTubeChannelURL: '',
			},
			formIsValid: false,
			countries,
			intentions: IntentionList,
			selectedCountry: '',
		};
	},

	computed: {
		selectedOptions() {
			return this.intentions.filter((intention) => intention.selected).map((intention) => intention.name);
		},
	},

	watch: {
		form: {
			async handler() {
				this.formIsValid = await this.$refs.observer.validate({ silent: true });
			},
			deep: true,
		},
		selectedCountry(value) {
			this.form.Country = value;
		},
	},

	async mounted() {
		try {
			const signupData = await this.getSignupData(this.invitationCode);
			if (signupData.Status === 2) throw new Error('alreadyRegistered');
			this.form = { ...this.form, ...signupData };
		} catch (error) {
			this.$router.push({ name: 'Landing' });

			if (error.message === 'alreadyRegistered') {
				this.$notify({
					duration: 6000,
					type: 'error',
					title: 'ERROR',
					text: 'User is already registered',
				});
			} else {
				this.$notify({
					duration: 6000,
					type: 'error',
					title: 'ERROR',
					text: 'your invitation code is invalid',
				});
			}
		}
	},

	methods: {
		...mapActions('auth', [
			'signup',
			'finishingSignup',
			'getSignupData',
		]),
		async signup() {
			await this.finishingSignup({ ...this.form, InvitationCode: this.invitationCode, Intention: this.selectedOptions.join(',') });
			this.$router.push({ name: 'Dashboard' });
		},
	},
};
</script>

<style lang="scss" scoped>
.section {

	.description-link {
		color: $color-primary;
	}

	.line {
		width: 100%;
		height: 1px;
		background-color: $color-accent-300;
		margin: $gap-4 0;
	}

	.fields {
		display: flex;
		flex-wrap: wrap;
		margin-top: $gap-2;

		.field {
			border: 1px solid $color-accent-300;
			padding: $gap-1 $gap-2;
			font-size: $size-7;
			border-radius: 8px;
			margin: $gap-05;
			cursor: pointer;

			&.is-active {
				background-color: $color-accent-300;
			}

			&:hover {
				background-color: $color-accent-300;
				transition: 300ms ease-in-out;
			}
		}
	}

	/deep/ .v-select {
		border: 1px solid $color-accent-300;
		border-radius: 8px;
		height: 48px;

		&:hover {
			border: 1px solid $color-accent-400;
		}
	}

	/deep/ .vs__dropdown-toggle {
		padding: 0;

		input, span {
			color: $color-accent-600;
		}
	}

	label {
		input {
			display: none;
		}

		.checkbox-custom {
			height: 32px;
			font-size: $size-7;
			color: $color-accent-400;
			font-weight: $fontWeight-medium;
			text-decoration: none;
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1px solid $color-accent-300;
			border-radius: 8px;
			padding: 0 $gap-15;
			margin: $gap-1;
			transition: 475ms;
			cursor: pointer;

			&:hover {
				background-color: $color-accent-300;
			}

			&.is-active {
				background-color: $color-accent-300;
			}
		}
	}

	form {
		width: 650px;
	}

	.section-body {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		.section-title,
		.section-subtitle {
			text-align: center;
		}

		.section-description {
			margin-bottom: $gap-1;
			margin-left: $gap-05;
		}

		.section-items {
			display: flex;
			flex-direction: column;

			.item {
				margin-top: $gap-4;

				&.button {
					margin-top: $gap-65;
				}

				&.section-description {
					text-align: center;
				}

				&.name {
					display: flex;
					justify-content: space-between;
				}
			}
		}

		p {
			width: 100%;
			display: flex;
			align-items: center;
			font-size: $size-6;
		}
	}

	.section-footer {
		padding-top: $gap-100 * 1.5;
	}
}
</style>
